import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as reportRunsApi from '../api/reportRuns';
import * as reportProfilesApi from '../api/reportProfiles';

export function ReportRunner() {
  const { profileId } = useParams();
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [profile, setProfile] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRunModal, setShowRunModal] = useState(false);
  const [running, setRunning] = useState(false);
  const [runForm, setRunForm] = useState({
    periodStart: '',
    periodEnd: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedOrg || !profileId || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [profileData, runsData] = await Promise.all([
        reportProfilesApi.getReportProfile(selectedOrg.id, profileId),
        reportRunsApi.getReportRuns(selectedOrg.id, { profileId })
      ]);
      setProfile(profileData);
      setRuns(runsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, profileId, canReadAssets]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenRun = () => {
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setRunForm({
      periodStart: firstOfMonth.toISOString().split('T')[0],
      periodEnd: lastOfMonth.toISOString().split('T')[0]
    });
    setShowRunModal(true);
  };

  const handleStartRun = async () => {
    if (!selectedOrg || running) return;
    setRunning(true);
    setError(null);
    try {
      await reportRunsApi.createReportRun(selectedOrg.id, {
        profileId,
        profileVersion: profile?.currentVersion || 1,
        periodStart: runForm.periodStart,
        periodEnd: runForm.periodEnd
      });
      setShowRunModal(false);
      setTimeout(() => loadData(), 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleDownload = async (run) => {
    if (!selectedOrg || !run.artifactId) return;
    try {
      const downloadInfo = await reportRunsApi.getArtifactDownload(selectedOrg.id, run.artifactId);
      alert(`Download: ${downloadInfo.filename}\nChecksum: ${downloadInfo.checksum}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return { bg: '#c6f6d5', color: '#276749' };
      case 'running': return { bg: '#bee3f8', color: '#2b6cb0' };
      case 'pending': return { bg: '#fefcbf', color: '#975a16' };
      case 'failed': return { bg: '#fed7d7', color: '#c53030' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to run reports.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title={profile?.name || 'Report Runner'}
        subtitle="Run reports and view history"
        breadcrumbs={[
          { label: 'Report Profiles', href: '/admin/report-profiles' },
          { label: 'Run Report' }
        ]}
      />

      <div style={styles.toolbar}>
        <button
          onClick={handleOpenRun}
          style={styles.runButton}
          data-testid="run-report-button"
        >
          Run Report
        </button>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : (
        <div style={styles.tableContainer}>
          <h3 style={styles.sectionTitle}>Run History</h3>
          <table style={styles.table} data-testid="runs-table">
            <thead>
              <tr>
                <th style={styles.th}>Run ID</th>
                <th style={styles.th}>Period</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Started</th>
                <th style={styles.th}>Completed</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(run => {
                const statusStyle = getStatusColor(run.status);
                return (
                  <tr key={run.id} style={styles.tr} data-testid={`run-${run.id}`}>
                    <td style={styles.td}>
                      <code style={styles.runId}>{run.id.slice(-8)}</code>
                    </td>
                    <td style={styles.td}>
                      {run.periodStart} to {run.periodEnd}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        background: statusStyle.bg,
                        color: statusStyle.color
                      }}>
                        {run.status}
                      </span>
                    </td>
                    <td style={styles.td}>v{run.profileVersion}</td>
                    <td style={styles.td}>
                      {run.startedAt ? new Date(run.startedAt).toLocaleString() : '-'}
                    </td>
                    <td style={styles.td}>
                      {run.completedAt ? new Date(run.completedAt).toLocaleString() : '-'}
                    </td>
                    <td style={styles.td}>
                      {run.status === 'completed' && run.artifactId && (
                        <button
                          onClick={() => handleDownload(run)}
                          style={styles.downloadButton}
                          data-testid={`download-${run.id}`}
                        >
                          Download
                        </button>
                      )}
                      {run.status === 'running' && (
                        <span style={styles.runningText}>Processing...</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {runs.length === 0 && (
                <tr>
                  <td colSpan="7" style={styles.emptyState}>No runs yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showRunModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="run-modal">
            <h3 style={styles.modalTitle}>Run Report</h3>
            <p style={styles.modalSubtitle}>Profile: {profile?.name}</p>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Period Start</label>
                <input
                  type="date"
                  value={runForm.periodStart}
                  onChange={(e) => setRunForm(prev => ({ ...prev, periodStart: e.target.value }))}
                  style={styles.input}
                  data-testid="period-start-input"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Period End</label>
                <input
                  type="date"
                  value={runForm.periodEnd}
                  onChange={(e) => setRunForm(prev => ({ ...prev, periodEnd: e.target.value }))}
                  style={styles.input}
                  data-testid="period-end-input"
                />
              </div>
            </div>
            <div style={styles.modalActions}>
              <button onClick={() => setShowRunModal(false)} style={styles.cancelButton}>
                Cancel
              </button>
              <button
                onClick={handleStartRun}
                disabled={running || !runForm.periodStart || !runForm.periodEnd}
                style={styles.submitButton}
                data-testid="start-run-button"
              >
                {running ? 'Starting...' : 'Start Run'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  toolbar: { display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' },
  runButton: { padding: '0.75rem 1.5rem', borderRadius: '6px', border: '1px solid #48bb78', background: '#48bb78', color: 'white', fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  sectionTitle: { margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem', fontSize: '0.875rem' },
  runId: { background: '#edf2f7', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontFamily: 'monospace' },
  statusBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  downloadButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.75rem', cursor: 'pointer' },
  runningText: { fontSize: '0.75rem', color: '#718096', fontStyle: 'italic' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '8px', padding: '1.5rem', minWidth: '450px', maxWidth: '500px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  modalTitle: { margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '600' },
  modalSubtitle: { margin: '0 0 1.5rem 0', fontSize: '0.875rem', color: '#718096' },
  formRow: { display: 'flex', gap: '1rem' },
  formGroup: { marginBottom: '1rem', flex: 1 },
  label: { display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#4a5568' },
  input: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem' },
  modalActions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  cancelButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  submitButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #48bb78', background: '#48bb78', color: 'white', fontSize: '0.875rem', cursor: 'pointer' }
};
